// Replaces the old right-column portlets shown on every blog post / event
// ("Recent Blog Posts", "Arkus Events", subscribe box): a three-column row after the post.
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { searchContent } from '@plone/volto/actions/search/search';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const useSearch = (key, options) => {
  const dispatch = useDispatch();
  const sub = useSelector((state) => state.search?.subrequests?.[key]);
  useEffect(() => {
    if (options && !sub?.loaded && !sub?.loading && !sub?.error) {
      dispatch(searchContent('', options, key));
    }
  }, [key, options, dispatch, sub?.loaded, sub?.loading, sub?.error]);
  return sub?.items || [];
};

const DATE = { year: 'numeric', month: 'long', day: 'numeric' };

const MoreFromArkus = ({ content }) => {
  const [today, setToday] = React.useState(null);
  useEffect(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    setToday(d.toISOString());
  }, []);
  const postsQ = useMemo(
    () => ({ portal_type: 'News Item', review_state: 'published', sort_on: 'effective',
             sort_order: 'descending', b_size: 6, metadata_fields: ['effective'] }),
    [],
  );
  const eventsQ = useMemo(
    () => today && { portal_type: 'Event', review_state: 'published', 'end.query': today,
                     'end.range': 'min', sort_on: 'start', sort_order: 'ascending', b_size: 3,
                     metadata_fields: ['start', 'end'] },
    [today],
  );
  const posts = useSearch('arkus-more-posts', postsQ);
  const events = useSearch(`arkus-more-events-${today}`, eventsQ);
  const here = flattenToAppURL(content?.['@id'] || '');
  const recent = posts.filter((i) => flattenToAppURL(i['@id']) !== here).slice(0, 5);
  return (
    <aside className="arkus-more" aria-label="More from Arkus">
      <div className="arkus-more-col">
        <h2 className="arkus-more-heading">Recent blog posts</h2>
        <ul>
          {recent.map((i) => (
            <li key={i['@id']}>
              <Link to={flattenToAppURL(i['@id'])}>{i.title}</Link>
              {i.effective && (
                <FormattedDate date={i.effective} format={DATE} className="arkus-more-date" />
              )}
            </li>
          ))}
        </ul>
        <Link className="arkus-more-all" to="/blog">All blog posts</Link>
      </div>
      <div className="arkus-more-col">
        <h2 className="arkus-more-heading">Upcoming events</h2>
        {events.length ? (
          <ul>
            {events.map((i) => (
              <li key={i['@id']}>
                <Link to={flattenToAppURL(i['@id'])}>{i.title}</Link>
                {i.start && (
                  <FormattedDate date={i.start} format={DATE} className="arkus-more-date" />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p>No upcoming events right now.</p>
        )}
        <Link className="arkus-more-all" to="/events">All events</Link>
      </div>
      <div className="arkus-more-col arkus-more-subscribe">
        <h2 className="arkus-more-heading">Subscribe</h2>
        <p>Get the Arkus Blog in your inbox with our semi-monthly newsletter.</p>
        <Link className="ui button arkus-more-button" to="/subscribe">Subscribe</Link>
      </div>
    </aside>
  );
};

export default MoreFromArkus;
